import * as React from "react";
import {
  FormSchema,
  IFieldElementUnion,
  IRepeatableElement,
  ISectionElement,
  isInputElement,
  isRepeatableElement,
  isStaticElement,
} from "@graffer-inc/form-schema";
import { SurveysFormSchemaValidator } from "@graffer-inc/cloud-gov-core";

import { useState } from "react";
import {
  SurveyEntity,
  SurveyFormPreview,
  BaseModal,
} from "@graffer-inc/smart-apply";
import { useModal } from "../../../../../utils/custom-hooks";
import {
  BaseProps,
  DefaultToolbarItemList,
  EditorRegistry,
  ElementEditConfig,
  ElementEditConfigRegistry,
  ElementRulesEditorViewComponentType,
} from "@graffer-inc/form-builder";
import { SecondaryButton } from "@graffer-inc/console-design-kit";
import {
  DefaultEditorRegistry,
  DefaultElementConfigRegistry,
  FormEditor,
} from "../../../../../common/form-builder-wrapper";
import { ElementRulesEditorView } from "../../../../organisms/common/form-editor/published-schema-update-custom-editor/element-rules-editor-view";
import { FeatureFlags } from "../../../../../feature-flags";
import { formMarkdownProcessorFactory } from "../../../../../utils/markdown/processor";
import { grafferSchemasSuportTagNames } from "@graffer-inc/form-generator";

type FormSettingProps = {
  survey: SurveyEntity;
  onSurveyUpdate: (survey: SurveyEntity) => void;
  isSafeEditOnly?: boolean;
  disabled: boolean;
  canUpdateSurvey: boolean;
  setIsFormUnsaved: (isFormUnsaved: boolean) => void;
};

export const FormSetting: React.FC<FormSettingProps> = ({
  survey,
  onSurveyUpdate,
  disabled,
  isSafeEditOnly,
  canUpdateSurvey,
  setIsFormUnsaved,
}) => {
  const ToolbarItems = DefaultToolbarItemList;
  const [currentFormSchema, setCurrentFormSchema] = useState<FormSchema>(
    survey.draft.formSchema,
  );
  const previewModal = useModal();
  const validator = new SurveysFormSchemaValidator(
    FormSchema.fromJSON(currentFormSchema),
  );
  const conditionalRuleViewMode = isSafeEditOnly ? "READONLY" : "EDITABLE";
  const errors = validator.validateDeep();

  const processor = formMarkdownProcessorFactory(grafferSchemasSuportTagNames);
  const surveyGrafferSchemasMarkdownProcessor = (content: string) => {
    return processor.processSync(content).result as React.ReactNode;
  };

  return (
    <>
      <div className="columns">
        <div className="column has-text-right">
          <div className="level-right buttons">
            <SecondaryButton text="プレビュー" onClick={previewModal.open} />
            {!disabled && canUpdateSurvey && (
              <SecondaryButton
                text={"変更を保存する"}
                disabled={
                  JSON.stringify(currentFormSchema) ===
                    JSON.stringify(survey.draft.formSchema) ||
                  !validator.isValid()
                }
                onClick={() => {
                  onSurveyUpdate(
                    SurveyEntity.fromJSON({
                      ...survey,
                      draft: {
                        ...survey.draft,
                        formSchema: FormSchema.fromJSON(currentFormSchema),
                      },
                    }),
                  );
                  setIsFormUnsaved(false);
                }}
              />
            )}
          </div>
        </div>
      </div>
      {errors.length > 0 && (
        <ul className="notification is-danger is-light">
          {errors.map((m) => (
            <li>{m}</li>
          ))}
        </ul>
      )}
      <div className="columns">
        <div className="column has-text-left">
          <FormEditor
            initialSchema={currentFormSchema}
            onChange={(schema) => {
              setCurrentFormSchema(FormSchema.fromJSON(schema));
              setIsFormUnsaved(true);
            }}
            editorOpt={{
              toolbarItemList: ToolbarItems,
              editConfigRegistry: new (class
                implements ElementEditConfigRegistry
              {
                get(
                  elem: IFieldElementUnion,
                  repeatable?: IRepeatableElement,
                ): ElementEditConfig {
                  if (isSafeEditOnly) {
                    return {
                      disableSort: true,
                      disableInsertion: true,
                      disableEdit: false,
                      disableDelete: true,
                      disableCopy: true,
                    };
                  }
                  return DefaultElementConfigRegistry.get(elem, repeatable);
                }
                getSectionEditConfig(elem: ISectionElement) {
                  if (isSafeEditOnly) {
                    return {
                      disableSort: true,
                      disableAdd: true,
                      disableEdit: true,
                      disableDelete: true,
                      disableCopy: true,
                    };
                  }
                  return DefaultElementConfigRegistry.getSectionEditConfig(
                    elem,
                  );
                }
              })(),
              editorRegistry: new (class implements EditorRegistry {
                getElementEditor(
                  elem: IFieldElementUnion,
                  parent?: IRepeatableElement | undefined,
                ): ElementRulesEditorViewComponentType | undefined {
                  if (
                    isSafeEditOnly &&
                    (isInputElement(elem) ||
                      isRepeatableElement(elem) ||
                      isStaticElement(elem))
                  ) {
                    return ElementRulesEditorView;
                  }
                  return DefaultEditorRegistry.getElementEditor(elem, parent);
                }
                // getBuilderFieldTemplate implementation same as default
                getBuilderFieldTemplate<
                  T extends IFieldElementUnion = IFieldElementUnion,
                >(elem: T, parent?: IRepeatableElement) {
                  return DefaultEditorRegistry.getBuilderFieldTemplate(
                    elem,
                    parent,
                  );
                }
                // getDropdownMenuContent implementation same as default
                getDropdownMenuContent():
                  | React.ComponentType<BaseProps>
                  | undefined {
                  return DefaultEditorRegistry.getDropdownMenuContent();
                }
              })(),
              sectionLimit: isSafeEditOnly
                ? currentFormSchema.root.length
                : undefined, // セクション追加ボタンを非表示にするために現在のセクション数を指定する。 see: https://github.com/graffer-inc/graffer-schemas/blob/70784b3c071948bd221f8268b58112603a8d87fd/libs/packages/form-builder/lib/elements/main-pane/section/section-tabs.tsx#L119-L120
              supportAllowedTermValidationForDateInput: true,
              supportYearMonthInput: true,
              supportFileInput: true,
              supportCopyValues: true,
              supportPostalCode: true,
              supportDescription: true,
              supportImage: FeatureFlags.ENABLE_GRAFFER_SCHEMAS_STATIC_IMAGE,
              conditionalRuleViewMode: conditionalRuleViewMode,
              supportSwitchEditorModeForSelectableInput: true,
              supportSuffix: true,
              markdownConfig: {
                initialValueForCommonProperties: true,
                initialValueForStaticDescription: true,
                showMarkdownApplyBanner: true,
                markdownApplyBannerNote:
                  "「有効化する」ボタンを押すと使用可能です。必要に応じて説明文を調整後、「変更を保存する」ボタンを押すとアンケートに反映されます。なお、変更を保存すると元に戻せません。",
                markdownProcessor: surveyGrafferSchemasMarkdownProcessor,
              },
            }}
          />
        </div>
      </div>
      <BaseModal
        title={"フォームプレビュー"}
        isActive={previewModal.isActive}
        onClose={previewModal.close}
      >
        <section className="modal-card-body">
          {previewModal.isActive && (
            <SurveyFormPreview currentFormSchema={currentFormSchema} />
          )}
        </section>
        <section className="modal-card-foot">
          <button
            type="button"
            className="button is-fullwidth"
            onClick={previewModal.close}
          >
            閉じる
          </button>
        </section>
      </BaseModal>
    </>
  );
};
